import { create } from "zustand";

import { defaultChangesState } from "./constants";
import { ChangesState, EntityChangeWithId } from "./types";

/**
 * Zustand store for managing collapse/expand state of individual changes
 * in the CodeChanges component. Handles state persistence during pagination
 * and component unmounting.
 */
type UseChangesStore = {
  /** Map of change IDs to their state (collapsed/expanded status and group states) */
  changes: Map<string, ChangesState>;
  /** Determines if any changes are currently collapsed */
  hasCollapsedChanges: () => boolean;
  /** Collapse all changes */
  collapseAll: () => void;
  /** Expand all changes */
  expandAll: () => void;
  /** Get the state of a specific change by ID. Returns undefined if not found. */
  getChange: (id: string) => ChangesState | undefined;
  /** Toggle the collapsed state of a specific change */
  toggleChange: (id: string) => void;
  /** Update the collapsed groups for a specific change */
  updateChangeGroups: (id: string, collapsedGroups: Set<string>) => void;
  /** Set changes from a list of entities, preserving existing states where possible */
  setChanges: (edges: EntityChangeWithId[]) => void;
};

export const useChangesStore = create<UseChangesStore>()((set, get) => ({
  changes: new Map<string, ChangesState>(),
  hasCollapsedChanges: () => {
    // Collapse changes on first load
    if (!get().changes.size) return true;

    const changes = Array.from(get().changes.values());
    return changes.some((change) => change.collapsed);
  },
  setChanges: (edges) => {
    set((state) => {
      const newChanges = new Map(state.changes);
      
      // Determine if new changes should be collapsed based on current state
      // Check if any existing changes are collapsed, or if there are no changes (first load)
      const shouldCollapseNewChanges = !state.changes.size || 
        Array.from(state.changes.values()).some((change) => change.collapsed);

      edges.forEach((edge) => {
        const id = edge.id;
        const change = newChanges.get(id);
        if (change) return;

        const newChange = { ...defaultChangesState, collapsed: shouldCollapseNewChanges };
        newChanges.set(id, newChange);
      });

      return { changes: newChanges };
    });
  },
  collapseAll: () => {
    set((state) => {
      const newChanges = new Map();
      state.changes.forEach((change, key) => {
        newChanges.set(key, { ...change, collapsed: true });
      });

      return { changes: newChanges };
    });
  },
  expandAll: () => {
    set((state) => {
      const newChanges = new Map();
      state.changes.forEach((change, key) => {
        newChanges.set(key, { ...change, collapsed: false });
      });

      return { changes: newChanges };
    });
  },
  getChange: (id) => {
    return get().changes.get(id);
  },
  toggleChange: (id) => {
    set((state) => {
      const newChanges = new Map(state.changes);
      const change = newChanges.get(id);
      
      // If change doesn't exist, create it with default state and toggle it
      if (!change) {
        const newChange = { ...defaultChangesState, collapsed: !defaultChangesState.collapsed };
        newChanges.set(id, newChange);
      } else {
        const newChange = { ...change, collapsed: !change.collapsed };
        newChanges.set(id, newChange);
      }

      return { changes: newChanges };
    });
  },
  updateChangeGroups: (id, collapsedGroups) => {
    set((state) => {
      const newChanges = new Map(state.changes);
      const change = newChanges.get(id);
      
      // If change doesn't exist, create it with the provided collapsed groups
      if (!change) {
        const newChange = { ...defaultChangesState, collapsedGroups };
        newChanges.set(id, newChange);
      } else {
        const newChange = { ...change, collapsedGroups };
        newChanges.set(id, newChange);
      }

      return { changes: newChanges };
    });
  },
}));
